import { FC, useContext, useEffect, useRef, useState } from "react";
import { format } from "date-fns";

import useFetch from "src/hooks/useFetch";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";
import { AuthContext } from "src/context/authContext";

import { DocumentAPIResponse, DocumentUploadProps, FetchDocumentResponseType, UploadedDocumentsListing } from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastTypes } from "src/types";
import { ACCEPTED_FILE_EXTENSIONS, MAX_FILE_SIZE_IN_BYTES } from "src/constants";
import { downloadFileAccordingToExtension } from "src/helpers/downloadFile";



const DocumentUpload: FC<DocumentUploadProps> = ({ caseId, caseDocuments, setNotes,refreshCall }): JSX.Element => {  
  const [caseFile, changeCaseFile] = useState<FileList | null>(null);
  const [fileName, changeFileName] = useState<string>("");
  const [caseDocumentsList, changeCaseDocumentsListing] = useState<UploadedDocumentsListing[]>(typeof(caseDocuments) !== 'string' ? caseDocuments : []);
  const [uploadFileInputError, changeUploadFileInputError] = useState<string | null>(null);

  const ref = useRef<HTMLInputElement>(null);
  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const { isManufacturer } = useContext(AuthContext);

  const reset = () => {
    changeCaseFile(null);
    if (ref && ref.current) {
      ref.current.value = ""
    }
  };

  const onUplaodCalled = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const caseFileExt = `.${files[0].name.split(".").pop()}`;
      const isExtAccepted = Object.keys(ACCEPTED_FILE_EXTENSIONS).find((ext) => caseFileExt === ext);
      if (!isExtAccepted) {
        changeUploadFileInputError(`Not an acceptable format. The accepted formats are ${Object.keys(ACCEPTED_FILE_EXTENSIONS).toString()}`);
      } else if (files[0].size > MAX_FILE_SIZE_IN_BYTES) {
        changeUploadFileInputError("File size exceeds 25MB limit");
      } else {
        changeUploadFileInputError(null);
        changeCaseFile(files)
      }
    }
  }

  const clearDataAndForm = () => {
    changeCaseFile(null);
    reset();
  };





  const { loading: uploadFileLoading, error: uploadFileError, data: uploadFileResponse, mutate: uploadCaseDocument } =
    useFetch<DocumentAPIResponse>(APIEndpoints.UPLOAD_CASE_FILE);

  useEffect(() => {
    if (uploadFileResponse || uploadFileError) {
      hideLoader();
      if(uploadFileError) {
        changeCaseFile(null);
        reset();
      }
      if(uploadFileResponse) {
        const { message, data, } = uploadFileResponse;
        setNotes((prevNotes) => [...prevNotes, data.note]);
        changeCaseDocumentsListing((prevList) => [
          ...prevList,
          {
            ...data,
            created_at: format(new Date(), `MM/dd/yyyy hh:mm aa`),
            type: "",
            id: "",
          }
        ]);
        showToast([message], ToastTypes.SUCCESS);
        reset();
        refreshCall && refreshCall()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileResponse, uploadFileError]);

  const uploadForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (caseFile) {
      setLoader();
      const formData = new FormData();
      formData.append('file', caseFile[0]);
      uploadCaseDocument(formData, { caseId });
    }
  };





  const { data: fetchDocumentData, error: fetchDocumentError, mutate: fetchDocument } =
    useFetch<FetchDocumentResponseType>(APIEndpoints.FETCH_DOCUMENT, {}, {}, false);

  useEffect(() => {
    if (fetchDocumentData || fetchDocumentError) {
      hideLoader();
      if (fetchDocumentData) {
        const { data } = fetchDocumentData;
        downloadFileAccordingToExtension(data, fileName);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDocumentData]);

  const showFetchedFile = (blob_id: number, fileName: string, fileType: string) => {
    setLoader();
    changeFileName(fileName);
    fetchDocument({}, {}, { blob_id, case_id: caseId, type: fileType });
  };





  const getDateTimeFormat = (dateAndTime: string) => {
    if (dateAndTime) {
      return format(new Date(dateAndTime), "MM/dd/yyyy") + " " + format(new Date(dateAndTime), "p");
    }
  };

  return (
    <div className="card rounded-1">
      <div className="card-header bg-success-subtle">
        <h4 className="mb-0 text-black">Documents</h4>
      </div>

      <div className="card-body">
        <div className={`container-fluid case-notes-listing ${caseDocumentsList && caseDocumentsList.length > 0 ? 'mb-4' : ''}`}>
          {typeof(caseDocuments) !== 'string' && caseDocumentsList && caseDocumentsList.length > 0 ? 
            caseDocumentsList.map((doc, index) => (
              <div key = {index} className={`row py-1 align-items-center ${index % 2 === 0 ? 'bg-secondary-subtle' : ''}`}>
                <div className="col-7">
                  <button
                    type="button"
                    onClick={() => showFetchedFile(doc.blob_id, doc.filename, doc.type || 'fetch')}
                    className="btn btn-link link-underline link-underline-opacity-0 link-offset-0-hover link-underline-opacity-75-hover p-0 align-baseline fs-6"
                  >
                    {doc && doc.type === "clinical_document" ? "Clinical Document - " + doc.filename : doc.filename}
                  </button>
                </div>
                <div className="col-2 text-start">
                  <p className="mb-0 fs-6">{doc ? `${doc.size_in_kb} kb` : ''}</p>
                </div>
                <div className="col-3">
                  <p className="mb-0 fs-6">{doc ? getDateTimeFormat(doc.created_at) : ''}</p>
                </div>
              </div>
            ))
          : <div> <h3 className="fw-bold">{typeof(caseDocuments) === 'string'  && caseDocuments}</h3></div>}
        </div>

        {!isManufacturer &&
          <div className="card">
          <div className="card-header bg-success-subtle">
            <h3 className="mb-0 text-black">
              <i className="bi bi-cloud-arrow-up-fill fs-5 text-end"></i>{' '}
              Upload New Document
            </h3>
          </div>

          <div className="card-body">
            <form onSubmit={uploadForm}>
              <div className="input-group mb-2">
                <input 
                  type="file" id="fileUpload" className="d-none" ref={ref}
                  onChange={onUplaodCalled}
                />
                <input
                  type="text" className="form-control bg-secondary-subtle" id="fileUpload"
                  value={caseFile && caseFile.length > 0 ? caseFile[0].name : ""}
                />
                <div className="">
                  {!caseFile ? (
                    <button type="button" onClick={() => ref.current ? ref.current.click() : null} className="btn btn-light">Choose a file</button>
                  ) : null}
                  {caseFile ? (
                    <>
                      <button
                        className="btn btn-outline-secondary me-2" onClick={clearDataAndForm} type="button"
                        id="fileUpload" disabled={uploadFileLoading}
                      >
                        Clear
                      </button>
                      <button type="button" onClick={() => ref.current ? ref.current.click() : null} className="btn btn-light">
                        Change
                      </button>
                    </>
                  ) : null}
                  <button
                    className="btn btn-blue"
                    type="submit"
                    disabled={!caseFile || uploadFileLoading}
                  >
                    {uploadFileLoading ? (
                      <i
                        className="spinner-border me-2"
                        style={{ width: "16px", height: "16px", fontSize: "9px" }}
                      ></i>
                    ) : null}
                    Upload
                  </button>
                </div>
              </div>
              {uploadFileInputError && <p className="d-block text-danger mb-0">{uploadFileInputError}</p>}
              {/* <div className="input-group d-flex flex-wrap flex-md-nowrap">
                <input type="file" className="form-control w-100" name="caseDocument"
                       onChange={(e) => changeCaseFile(e.target.files)} ref={ref}/>
                <button
                    disabled={!caseFile || caseFile.length === 0 || uploadFileLoading}
                    className="btn btn-blue mt-2 mt-md-0"
                    type="submit"
                    id="inputGroupFileAddon04"
                >
                  <div className="row align-items-center px-1">
                    {uploadFileLoading ? (
                      <div className="spinner-border text-light col-3" role="status">
                        <span className="sr-only"></span>
                      </div>
                    ) : null}
                    <p className="col-auto mb-0"> Upload </p>
                  </div>
                </button>
              </div> */}
            </form>
          </div>

        </div>
        }
      </div>
    </div>
  );
};

export default DocumentUpload;